import type { HttpError, HttpErrorRecord } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import { useMutation } from "@tanstack/react-query"
import type { UseMutationOptions } from "@tanstack/react-query"

import type { UseUploadMutationVariables } from "../services.types"

type UseUploadMetersOptions = Omit<
  UseMutationOptions<
    unknown,
    HttpError<HttpErrorRecord>,
    UseUploadMutationVariables
  >,
  "mutationFn"
>

const uploadMeters = async ({
  file,
  organizationId,
}: UseUploadMutationVariables) => {
  try {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("organization_id", organizationId ?? "")

    const response = await httpClient.post(
      "/api/v1/data_manager/meter_uploads",
      formData
    )

    return response
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

/**
 * Uploads a CSV file of meter data
 *
 * @example
 * const {
 *   error,
 *   isError,
 *   isLoading,
 *   mutate
 * } = useUploadMeters(options)
 *
 * mutate({ file, organizationId })
 */
export const useUploadMeters = (options: UseUploadMetersOptions = {}) => {
  const mutationOptions = options

  const mutation = useMutation({
    ...mutationOptions,
    meta: { ...mutationOptions.meta, handlesOwnErrors: true },
    mutationFn: uploadMeters,
  })

  return mutation
}
